import decodeRestResponse from './decode-rest-response';
import getApiFetch from './getApiFetch';

/**
 * Given a list of post types, fetches all posts for the given post types.
 * @param {array} postTypes - An array of post types to fetch.
 * @param {object} endpoints - Optional. An association of post types as keys to endpoint bases, e.g., `{ grants: '/knight-foundation-app/v1/grants' }`.
 * @returns {object} An object containing results, keyed by post type slug.
 */
const fetchAll = async (postTypes, endpoints = {}) => {
  // Get the apiFetch function with the REST base middleware pre-configured.
  const apiFetch = getApiFetch();

  /**
   * A helper function for getting a page of data.
   * @param {string} postType - The post type to fetch.
   * @param {number} page - The page of data to fetch.
   * @returns {Promise} A promise that resolves to an object containing the fetched data and the post type.
   */
  const getPageOfData = async (postType, page) => {
    const basePath = endpoints[postType] || `/wp/v2/${postType}`;
    const path = `${basePath}?per_page=100&page=${page}`;
    const data = await apiFetch({ path });
    return {
      data,
      postType,
    };
  };

  // Loop over the post types array and fetch the first 100 posts and total pages for each.
  const initialFetches = postTypes.map(async (postType) => {
    // Negotiate the endpoint path for this post type.
    const basePath = endpoints[postType] || `/wp/v2/${postType}`;
    const path = `${basePath}?per_page=100`;
    const response = await apiFetch({
      parse: false,
      path,
    });
    const totalPages = parseInt(response.headers.get('X-WP-TotalPages'), 10);
    const data = await response.json();

    return {
      data,
      postType,
      totalPages,
    };
  });

  // Loop over the initial fetches and add additional fetches for more pages, as necessary.
  const initialData = await Promise.all(initialFetches);
  const fetches = initialData.reduce((acc, item) => {
    const {
      data,
      postType,
      totalPages,
    } = item;

    // Add the JSON data for this fetch.
    acc.push({
      data,
      postType,
    });

    // Make additional fetches, as necessary, for remaining data.
    for (let i = 2; i <= totalPages; i += 1) {
      acc.push(getPageOfData(postType, i));
    }
    return acc;
  }, []);

  // Ensure that all of the data has loaded before proceeding.
  const allPosts = await Promise.all(fetches);

  // Combine each individual chunk of posts into a single object keyed by post type.
  return allPosts.reduce((acc, item) => {
    const {
      data,
      postType,
    } = item;

    // Either augment the existing list or initialize it with data.
    const oldData = acc[postType] ? acc[postType] : [];
    acc[postType] = [
      ...oldData,
      ...decodeRestResponse(data),
    ];

    return acc;
  }, {});
};

export default fetchAll;
